import React from 'react';
import { Navigate } from 'react-router-dom';
import { AssessmentProvider, useAssessmentContext, useAssessmentTooltips } from './AssessmentContext';
import TestDashboard from './dashboard/TestDashboard';
import TemplateBuilder from './Templates/TemplateBuilder';
import QuestionBankManager from './QuestionBank/QuestionBankManager';
import HRUploadManager from './HRUpload/HRUploadManager';
import MemberAssessmentDashboard from './student/MemberAssessmentDashboard';
import Layout from '../../components/Layout/Layout';
import { useUser } from '../../contexts/UserContext';
import ResultsEvaluation from './evaluation/ResultsEvaluation';
import {
  isAssessmentAdmin,
  isAssessmentBlocked,
  isAssessmentStudent,
} from '../../utils/assessmentStudent';

const FULL_HEIGHT_MODULES = new Set(['templates', 'questionBank', 'hrUpload']);

const AssessmentContent = () => {
  useAssessmentTooltips();
  const { activeModule, moduleReturnTo, returnFromModule, selectedTestId } = useAssessmentContext();
  const useFullHeightLayout = FULL_HEIGHT_MODULES.has(activeModule);

  return (
    <div
      className={`bg-[#fcfcfc] flex flex-col max-lg:h-auto max-lg:overflow-visible ${
        useFullHeightLayout
          ? 'h-full min-h-0 overflow-hidden'
          : 'min-h-0 overflow-visible'
      }`}
    >
      <div
        className={`w-full max-lg:flex-initial max-lg:h-auto max-lg:overflow-visible ${
          useFullHeightLayout ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-0 overflow-visible'
        }`}
      >
        {activeModule === 'inventory' && <TestDashboard />}
        {activeModule === 'questionBank' && (
          <QuestionBankManager onBack={returnFromModule} returnToTemplates={moduleReturnTo === 'templates'} />
        )}
        {activeModule === 'templates' && (
          <TemplateBuilder onBack={returnFromModule} />
        )}
        {activeModule === 'evaluation' && (
          <ResultsEvaluation
            assessmentId={selectedTestId}
            onBack={returnFromModule}
          />
        )}
        {activeModule === 'hrUpload' && (
          <HRUploadManager onBack={returnFromModule} />
        )}
      </div>
    </div>
  );
};

const AssessmentPage = () => {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (isAssessmentBlocked(user)) {
    return <Navigate to="/tickets" replace />;
  }

  const showAdminInventory = isAssessmentAdmin(user);

  return (
    <AssessmentProvider>
      <Layout noSidebar={true}>
        {showAdminInventory ? <AssessmentContent /> : <MemberAssessmentDashboard/>}
      </Layout>
    </AssessmentProvider>
  );
};

export default AssessmentPage;
